import {
  FOProfile,
  GuestInformation,
  GuestProfileList,
  IProfileCreateResponse,
  ProfileVisitHistory,
} from '../../modules/front-office/interface/fo-profile-interface';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';
import { frontOfficeApi } from './front-office.api.rtk';
import { api } from '@services/api';
import { setACCGCustomer } from '@store/slices/accounting-and-finance/accg-customer.slice.ts';

export const profileApi = api
  .enhanceEndpoints({
    addTagTypes: ['Profile', 'Profile Details', 'Profile History'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      postProfile: builder.mutation({
        query: (payload) => ({
          url: 'profiles',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(profileApi.util.invalidateTags(['Profile']));
            dispatch(
              frontOfficeApi.util.invalidateTags(['Account Masterlist']),
            );
          }
        },
        transformResponse(response: IProfileCreateResponse) {
          return response.data.profileID;
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateProfile: builder.mutation({
        query: (payload) => ({
          url: 'profiles',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(profileApi.util.invalidateTags(['Profile']));
            dispatch(
              frontOfficeApi.util.invalidateTags([
                'Account Masterlist',
                'Specific Booking',
              ]),
            );
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      deleteProfile: builder.mutation({
        query: (profileID) => ({
          url: `profiles/${profileID}`,
          method: 'DELETE',
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            await Promise.all([
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Account Masterlist',
                  'Specific Booking',
                  'Bookings',
                ]),
              ),
            ]);
            dispatch(profileApi.util.invalidateTags(['Profile']));
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateContactInformations: builder.mutation({
        query: (payload) => ({
          url: 'profiles/contactInformations',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Profile'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getProfile: builder.query({
        query: (params) => `profiles/${params}`,
        providesTags: ['Profile'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            const { data: response } = await queryFulfilled;

            if (!response) return;
          } catch (error) {
            console.error(error);
          }
        },
        transformResponse: async (response: GuestProfileList) => {
          return response;
        },
        forceRefetch() {
          return true;
        },
      }),
      getProfileForAll: builder.query({
        query: () => `profiles`,
        providesTags: ['Profile'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const { data: response } = await queryFulfilled;

            if (!response) return;

            dispatch(setACCGCustomer(response.data.profiles));
          } catch (error) {
            console.error(error);
          }
        },
        transformResponse: async (response: GuestProfileList) => {
          return response;
        },
        forceRefetch() {
          return true;
        },
      }),
      getProfileDetails: builder.query({
        query: (params) => `profiles/${params}`,
        providesTags: ['Profile Details'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: async (response: FOProfile) => {
          return response;
        },
        forceRefetch() {
          return true;
        },
      }),
      getProfileVisitHistory: builder.query({
        query: (query) => {
          return `profiles/${query}/visits`;
        },
        providesTags: ['Profile History'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: async (response: ProfileVisitHistory) => {
          return response;
        },
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
      }),
      getRetention: builder.query({
        query: (params: { profileID: string; includeCollections: boolean }) => {
          return {
            url: `profiles/${params?.profileID}/retention`,
            headers: {
              includeCollections: `${params?.includeCollections}`,
            },
          };
        },
        providesTags: ['Profile'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: async (response: GuestInformation) => {
          return response?.data?.retention;
        },
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
      }),
    }),
  });

export const {
  usePostProfileMutation,
  useUpdateProfileMutation,
  useDeleteProfileMutation,
  useUpdateContactInformationsMutation,
  useLazyGetProfileQuery,
  useLazyGetProfileDetailsQuery,
  useGetProfileQuery,
  useGetProfileForAllQuery,
  useGetProfileDetailsQuery,
  useLazyGetProfileVisitHistoryQuery,
  useGetProfileVisitHistoryQuery,
  useLazyGetRetentionQuery,
} = profileApi;
